// app/javascript/controllers/donation_controller.js
import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = [
    "preset",
    "customAmount",
    "customRadio",
    "customContainer",
    "feeCoverage",
    "feeCoverageLabel",
    "feeCoverageAmount",
    "donationWithoutFees",
    "tributeCheckbox",
    "tributeSection",
    "submitButton",
    "donationTotal",
    "amount"
  ]

  connect() {
    // Set initial donation amount (default to preset or 25)
    console.log("donation controller connected")

    // Check if a donation amount is already selected
    const selectedPreset = this.presetTargets.find(target => target.checked)
    
    // Check if custom radio is selected
    const customRadioChecked = this.hasCustomRadioTarget && this.customRadioTarget.checked
    
    if (customRadioChecked) {
      // Custom amount is selected
      this.donationAmount = this.hasCustomAmountTarget && this.customAmountTarget.value ? 
        parseFloat(this.customAmountTarget.value) : 25
      // Show custom amount field
      if (this.hasCustomContainerTarget) {
        this.customContainerTarget.classList.remove('d-none')
      }
    } else if (selectedPreset) {
      this.donationAmount = parseFloat(selectedPreset.value)
      // Hide custom amount field
      if (this.hasCustomContainerTarget) {
        this.customContainerTarget.classList.add('d-none')
      }
    } else {
      this.donationAmount = 25
      // Hide custom amount field
      if (this.hasCustomContainerTarget) {
        this.customContainerTarget.classList.add('d-none')
      }
    }

    // Apply active class to the appropriate donation box
    // Views may use .donation-box or .amount-box for styling
    const hasDonationBoxes = document.querySelector('.donation-box') !== null || document.querySelector('.amount-box') !== null
    if (hasDonationBoxes) {
      this.updateDonationBoxVisuals()
    } else {
      this.updateDonationOptionVisuals()
    }
    this.updateDonationSummary()
  }

  selectPreset(event) {
    // Get the radio button - may be the event target or inside the clicked wrapper
    var radio = event.currentTarget.querySelector('input[type="radio"]') || event.currentTarget
    radio.checked = true
    this.donationAmount = parseFloat(radio.value)

    // Update visual state
    var hasDonationBoxes = document.querySelector('.donation-box') !== null || document.querySelector('.amount-box') !== null
    if (hasDonationBoxes) {
      this.updateDonationBoxVisuals()
    } else {
      this.updateDonationOptionVisuals()
    }

    // Clear custom amount and hide the custom amount field
    if (this.hasCustomAmountTarget) {
      this.customAmountTarget.value = ""
    }
    if (this.hasCustomContainerTarget) {
      this.customContainerTarget.classList.add('d-none')
    }

    this.updateDonationSummary()

    // Save donation amount to server for accurate fee calculations
    this.saveDonationToServer(this.donationAmount)
  }

  updateDonationOptionVisuals() {
    // For views using .donation-option instead of .donation-box
    const donationOptions = document.querySelectorAll('.donation-option')
    
    // Remove active class from all options
    donationOptions.forEach(option => {
      option.classList.remove('active')
    })
    
    // Add active class to the selected one
    this.presetTargets.forEach(target => {
      if (parseFloat(target.value) === this.donationAmount) {
        const option = target.closest('.donation-option')
        if (option) {
          option.classList.add('active')
        }
        target.checked = true
      }
    })
  }

  updateDonationBoxVisuals() {
    // Find all donation boxes (supports both .donation-box and .amount-box)
    var selector = '.donation-box, .amount-box'
    var donationBoxes = document.querySelectorAll(selector)

    // Remove active class from all boxes
    donationBoxes.forEach(function(box) {
      box.classList.remove('active', 'bg-blue', 'text-white', 'border-blue')
      box.classList.add('border-light-subtle')
    })

    // Add active class to the selected one
    var amount = this.donationAmount
    this.presetTargets.forEach(function(target) {
      if (parseFloat(target.value) === amount) {
        var label = target.closest('label')
        if (label) {
          var box = label.querySelector('.donation-box') || label.querySelector('.amount-box')
          if (box) {
            box.classList.add('active', 'bg-blue', 'text-white', 'border-blue')
            box.classList.remove('border-light-subtle')
          }
        }
        target.checked = true
      }
    })
  }

  updateCustomAmount(event) {
    const value = event.currentTarget.value.trim()

    if (value) {
      const amount = parseFloat(value)

      // Validate: must be a positive number
      if (isNaN(amount) || amount < 0) {
        // Invalid amount - keep current donation amount but don't update
        // The field will show the invalid value but summary won't update
        return
      }

      this.donationAmount = amount

      // Clear selection of presets
      this.presetTargets.forEach(target => {
        target.checked = false
      })

      // Clear active state from all donation boxes
      var allBoxes = document.querySelectorAll('.donation-box, .amount-box')
      allBoxes.forEach(function(box) {
        box.classList.remove('active', 'bg-blue', 'text-white', 'border-blue')
        box.classList.add('border-light-subtle')
      })
      var donationOptions = document.querySelectorAll('.donation-option')
      donationOptions.forEach(function(option) {
        option.classList.remove('active')
      })

      this.updateDonationSummary()

      // Debounce server save for custom amounts (wait 500ms after typing stops)
      clearTimeout(this.customAmountTimeout)
      this.customAmountTimeout = setTimeout(() => {
        this.saveDonationToServer(amount)
      }, 500)
    } else {
      // If custom amount is cleared, keep current amount but don't update to 0
      // User must enter a valid positive amount
      this.donationAmount = 0
      this.updateDonationSummary()
    }
  }

  toggleCustomAmount(event) {
    if (this.hasCustomContainerTarget) {
      if (event.currentTarget.checked && event.currentTarget.value === "custom") {
        // Show custom amount field
        this.customContainerTarget.classList.remove('d-none')

        // Clear preset selection visual state
        const hasDonationBoxes = document.querySelector('.donation-box') !== null
        if (hasDonationBoxes) {
          const donationBoxes = document.querySelectorAll('.donation-box')
          donationBoxes.forEach(box => {
            box.classList.remove('active', 'bg-blue', 'text-white', 'border-blue')
            box.classList.add('border-light-subtle')
          })
        } else {
          const donationOptions = document.querySelectorAll('.amount-option')
          donationOptions.forEach(option => {
            option.classList.remove('active')
          })
        }

        // Add active state to custom option
        const customOption = event.currentTarget.closest('.amount-option')
        if (customOption) {
          customOption.classList.add('active')
        }

        // Pre-fill with current donation amount (or default to 25)
        const prefillAmount = this.donationAmount > 0 ? this.donationAmount : 25
        if (this.hasCustomAmountTarget) {
          this.customAmountTarget.value = prefillAmount
          this.customAmountTarget.focus()
          this.customAmountTarget.select()
        }

        // Keep the current donation amount
        this.donationAmount = prefillAmount
        this.updateDonationSummary()
      } else {
        // Hide custom amount field
        this.customContainerTarget.classList.add('d-none')
      }
    }
  }

  updateFeeCoverage(event) {
    this.updateDonationSummary()
  }

  saveDonationToServer(amount) {
    // Make AJAX call to save donation and get updated cart totals
    fetch('/shopping_cart/update_donation', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ amount: amount })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Keep the fee line hidden on the donate step
        var feeLine = document.getElementById('fee-line')
        if (feeLine) feeLine.style.display = 'none'
      }
    })
    .catch(error => {
      console.error('Error saving donation:', error)
    })
  }

  toggleTribute(event) {
    if (this.tributeCheckboxTarget.checked) {
      this.tributeSectionTarget.classList.remove('d-none')
    } else {
      this.tributeSectionTarget.classList.add('d-none')
    }
  }

  updateFrequency(event) {
    // Make the label of the selected frequency button active
    const labels = document.querySelectorAll('.btn-group .btn')
    labels.forEach(label => {
      label.classList.remove('active')
    })
    event.currentTarget.closest('.btn').classList.add('active')
  }

  updateDonationSummary() {
    // Calculate processing fee (5% + $0.35)
    const baseAmount = this.donationAmount || 0
    const fee = (baseAmount * 0.05) + 0.30

    // Calculate gross-up amount (always calculate it, regardless of checkbox)
    const feeCoverageAmount = (fee / (1 - 0.05)).toFixed(2)

    // Update the fee coverage label
    if (this.hasFeeCoverageAmountTarget) {
      this.feeCoverageAmountTarget.textContent = `$${feeCoverageAmount}`
    }

    if (this.hasDonationWithoutFeesTarget) {
      this.donationWithoutFeesTarget.textContent = `$${baseAmount.toFixed(2)}`
    }

    // Calculate total (include fee coverage only if enabled)
    const feeCoverageEnabled = this.hasFeeCoverageTarget && this.feeCoverageTarget.checked
    const feeCoverageValue = feeCoverageEnabled ? parseFloat(feeCoverageAmount) : 0
    const total = baseAmount + feeCoverageValue

    // Update the donation button
    if (this.hasDonationTotalTarget) {
      this.donationTotalTarget.textContent = `$${total.toFixed(2)}`
    }

    // Update the submit button text
    if (this.hasAmountTarget) {
      this.amountTarget.textContent = `$${total.toFixed(2)}`;
    }

    // Update the submit button based on donation amount and cart items
    // Use getSubmitButton() to handle mobile navigation repositioning
    const submitButton = this.getSubmitButton()
    if (submitButton) {
      // Check if there are cart items by looking for cart item elements
      const hasCartItems = document.querySelectorAll('.summary-item').length > 0
      // Check if this is a Giving Day checkout (has org-donation controllers)
      const isGivingDayCheckout = document.querySelectorAll('[data-controller~="org-donation"]').length > 0

      if (baseAmount > 0) {
        // Has donation - show Continue button
        submitButton.innerHTML = `Continue <i class="fa-solid fa-arrow-right ms-2"></i>`
        submitButton.disabled = false
      } else if (hasCartItems || isGivingDayCheckout) {
        // Has cart items or is Giving Day checkout - allow proceeding
        submitButton.innerHTML = isGivingDayCheckout ? `CONTINUE <i class="fa-solid fa-arrow-right ms-2"></i>` : `Skip Donation <i class="fa-solid fa-arrow-right ms-2"></i>`
        submitButton.disabled = false
      } else {
        // No cart items and no donation - disable button
        submitButton.innerHTML = `Skip Donation <i class="fa-solid fa-arrow-right ms-2"></i>`
        submitButton.disabled = true
      }
    }

    // Dispatch cartUpdated event so purchase summary controller can update
    // Don't include fee_coverage on donate step (fees are set on payment step)
    document.dispatchEvent(new CustomEvent('cartUpdated', {
      detail: {
        cart: {
          donation_amount: baseAmount,
          total: baseAmount
        }
      }
    }))

    // Update donation row in purchase summary
    var donationRow = document.getElementById('donation-amount-row')
    var donationSubtotal = document.querySelector('[data-purchase-summary-target="donationsSubtotal"]')
    if (donationRow) {
      donationRow.style.display = baseAmount > 0 ? '' : 'none'
    }
    if (donationSubtotal) {
      donationSubtotal.textContent = '$' + baseAmount.toFixed(2)
    }

    // Update grand total (items + donation, no fees on this step)
    var grandTotalEl = document.querySelector('[data-purchase-summary-target="grandTotal"]')
    if (grandTotalEl) {
      var itemsTotal = 0
      var itemsTotalEl = document.querySelector('[data-purchase-summary-target="itemsSubtotal"]')
      if (itemsTotalEl) {
        itemsTotal = parseFloat(itemsTotalEl.textContent.replace(/[^0-9.]/g, '')) || 0
      }
      grandTotalEl.textContent = '$' + (itemsTotal + baseAmount).toFixed(2)
    }
  }

  // Get the submit button - may be outside controller scope on mobile due to navigation repositioning
  getSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      return this.submitButtonTarget
    }
    // Fallback: search in mobile nav placeholder
    const mobileNav = document.getElementById('mobile-nav-placeholder')
    if (mobileNav) {
      return mobileNav.querySelector('[data-donation-target="submitButton"]')
    }
    return null
  }
}
